import { Input, VStack, Checkbox, Collapsible } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { useState } from "react"
import { useMutation } from "@apollo/client"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "@hydrologiq/shared/utils/toast"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import {
  composeCreatedTitle,
  composeCreatedDescription,
  composeFormHelperText,
  composePlaceholder,
} from "@hydrologiq/shared/copy/utils"

export interface CustomerCreationFormProps {
  onSuccess: (customer: gqlTypes.Customer) => void
  onCancel?: () => void
  onBack?: () => void
  enableSiteCreation?: boolean
  initialName?: string
  /** Custom label for the "create site" checkbox. Defaults to "Create a customer site and assign to order" */
  siteCheckboxLabel?: string
}

/**
 * Shared customer creation form component.
 * Can be used across different sections without tight coupling to routing or section-specific state.
 */
const CustomerCreationForm = ({
  onSuccess,
  onCancel,
  onBack,
  enableSiteCreation = true,
  initialName,
  siteCheckboxLabel,
}: CustomerCreationFormProps) => {
  const [error, setError] = useState<string | null>(null)
  const [createSite, setCreateSite] = useState(false)
  const [siteName, setSiteName] = useState("")
  const [customerName, setCustomerName] = useState(initialName || "")

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.CUSTOMER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.CUSTOMER],
    [COPY_KEYS.HEADINGS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE],
    [COPY_KEYS.HEADINGS.ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER],
    [COPY_KEYS.MESSAGES.CREATED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.CREATED],
    [COPY_KEYS.MESSAGES.SUCCESSFULLY_CREATED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.SUCCESSFULLY_CREATED],
    [COPY_KEYS.MESSAGES.CREATION_FAILED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.CREATION_FAILED],
    [COPY_KEYS.MESSAGES.VALIDATION_ERROR]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.VALIDATION_ERROR],
    [COPY_KEYS.MESSAGES.PLEASE_PROVIDE_NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.PLEASE_PROVIDE_NAME],
    [COPY_KEYS.MESSAGES.UNEXPECTED_ERROR]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.UNEXPECTED_ERROR],
    [COPY_KEYS.ACTIONS.CREATING]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.ACTIONS.CREATING],
    [COPY_KEYS.BUTTONS.CREATE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.BUTTONS.CREATE],
    [COPY_KEYS.COMMON.TO_THE_SYSTEM]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.COMMON.TO_THE_SYSTEM],
    [COPY_KEYS.COMMON.AND]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.COMMON.AND],
    [COPY_KEYS.COMMON.A]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.COMMON.A],
    [COPY_KEYS.FORMS.HELPER_TEXT_CREATE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.HELPER_TEXT_CREATE],
    [COPY_KEYS.FORMS.HELPER_TEXT_EDIT]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.HELPER_TEXT_EDIT],
    [COPY_KEYS.FORMS.PLACEHOLDER_ENTER_NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.PLACEHOLDER_ENTER_NAME],
    [COPY_KEYS.FORMS.PLEASE_PROVIDE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.PLEASE_PROVIDE],
    [COPY_KEYS.FORMS.INFORMATION_BELOW]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.INFORMATION_BELOW],
    [COPY_KEYS.LABELS.NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.NAME],
  })

  const [createCustomer, { loading }] = useMutation(gqlTypes.CreateCustomerDocument, {
    // Update Apollo cache to add the new customer
    update(cache, { data }) {
      if (!data?.createCustomer) return

      const newCustomer = data.createCustomer

      // Evict and refetch customers queries to ensure fresh data
      cache.modify({
        fields: {
          customers(existingCustomers = [], { toReference }) {
            const newCustomerRef = toReference(newCustomer)

            // Check if customer already exists
            if (existingCustomers.some((ref: any) => ref.__ref === newCustomerRef?.__ref)) {
              return existingCustomers
            }

            // Add new customer to the list
            return [newCustomerRef, ...existingCustomers]
          },
        },
      })
    },
  })

  const handleSubmit = async () => {
    let loadingToastId: string | undefined
    try {
      setError(null)

      // Show loading toast
      loadingToastId = showLoadingToast(
        `${copy[COPY_KEYS.ACTIONS.CREATING]} ${copy[COPY_KEYS.HEADINGS.CUSTOMER]}`,
        `${copy[COPY_KEYS.ACTIONS.CREATING]} ${copy[COPY_KEYS.HEADINGS.CUSTOMER].toLowerCase()} ${
          copy[COPY_KEYS.COMMON.TO_THE_SYSTEM]
        }...`
      )

      const name = customerName.trim()
      if (!name) {
        const errorMessage = `${copy[COPY_KEYS.MESSAGES.PLEASE_PROVIDE_NAME]} ${copy[
          COPY_KEYS.HEADINGS.CUSTOMER
        ].toLowerCase()}`
        setError(errorMessage)
        showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
        return false
      }

      // Validate site data if creating a site
      if (createSite) {
        if (!siteName || !siteName.trim()) {
          const errorMessage = `${copy[COPY_KEYS.MESSAGES.PLEASE_PROVIDE_NAME]} ${copy[
            COPY_KEYS.HEADINGS.SITE
          ].toLowerCase()}`
          setError(errorMessage)
          showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
          return false
        }
      }

      // Prepare mutation variables
      const customerInput: gqlTypes.CustomerInput = {
        name: name,
      }

      if (createSite) {
        customerInput.site = {
          name: siteName.trim(),
        }
      }

      const result = await createCustomer({ variables: { customer: customerInput } })

      if (result.data?.createCustomer) {
        const createdCustomer = result.data.createCustomer
        const hasSite = createdCustomer.sites && createdCustomer.sites.length > 0

        showSuccessToast(
          composeCreatedTitle(copy, COPY_KEYS.HEADINGS.CUSTOMER),
          composeCreatedDescription(copy, COPY_KEYS.HEADINGS.CUSTOMER, name) +
            (hasSite ? ` ${copy[COPY_KEYS.COMMON.AND]} ${copy[COPY_KEYS.HEADINGS.SITE].toLowerCase()}` : "")
        )

        setError(null)
        setCreateSite(false)
        setSiteName("")
        setCustomerName("")

        // Pass created customer to parent
        onSuccess(createdCustomer)

        return true
      } else {
        const errorMessage = `${copy[COPY_KEYS.MESSAGES.CREATION_FAILED]} ${copy[
          COPY_KEYS.HEADINGS.CUSTOMER
        ].toLowerCase()}.`
        setError(errorMessage)
        showErrorToast(copy[COPY_KEYS.MESSAGES.CREATION_FAILED], errorMessage)
        return false
      }
    } catch (error) {
      let errorMessage =
        error instanceof Error
          ? error.message
          : `${copy[COPY_KEYS.MESSAGES.UNEXPECTED_ERROR]} ${copy[COPY_KEYS.HEADINGS.CUSTOMER].toLowerCase()}`

      if (errorMessage.includes("idx_customer_tenant_name") || errorMessage.includes("duplicate key")) {
        errorMessage = `A ${copy[COPY_KEYS.HEADINGS.CUSTOMER].toLowerCase()} with this name already exists. Please choose a different name.`
      }
      if (errorMessage.includes("idx_site_tenant_name_active")) {
        errorMessage = `A ${copy[COPY_KEYS.HEADINGS.SITE].toLowerCase()} with this name already exists. Please choose a different name.`
      }

      setError(errorMessage)
      showErrorToast(copy[COPY_KEYS.MESSAGES.CREATION_FAILED], errorMessage)
      return false
    } finally {
      // Always dismiss loading toast
      if (loadingToastId) {
        dismissToast(loadingToastId)
      }
    }
  }

  const handleCancel = () => {
    setError(null)
    setCreateSite(false)
    setSiteName("")
    setCustomerName("")
    if (onCancel) onCancel()
  }

  const handleBack = () => {
    setError(null)
    setCreateSite(false)
    setSiteName("")
    setCustomerName("")
    if (onBack) onBack()
  }

  function capitalize(arg0: string) {
    return arg0.charAt(0).toUpperCase() + arg0.slice(1)
  }

  return (
    <Form
      heading={`${copy[COPY_KEYS.ACTIONS.CREATING]} ${copy[COPY_KEYS.HEADINGS.CUSTOMER]}`}
      helperText={composeFormHelperText(copy, COPY_KEYS.HEADINGS.CUSTOMER, "create")}
      actionType="create"
      loading={loading}
      onSubmit={handleSubmit}
      onCancel={onCancel ? handleCancel : undefined}
      onBack={onBack ? handleBack : undefined}
    >
      <VStack gap={5}>
        {error && (
          <ErrorAlert
            title={`${copy[COPY_KEYS.HEADINGS.CUSTOMER]} ${copy[COPY_KEYS.MESSAGES.CREATION_FAILED]}`}
            message={error}
            onClose={() => setError(null)}
          />
        )}
        <Field label={copy[COPY_KEYS.LABELS.NAME]} required>
          <Input
            name="name"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder={composePlaceholder(copy, COPY_KEYS.FORMS.PLACEHOLDER_ENTER_NAME, COPY_KEYS.HEADINGS.CUSTOMER)}
          />
        </Field>

        {enableSiteCreation && (
          <>
            <Field>
              <Checkbox.Root checked={createSite} onCheckedChange={(e) => setCreateSite(!!e.checked)}>
                <Checkbox.HiddenInput />
                <Checkbox.Control />
                <Checkbox.Label>
                  {siteCheckboxLabel ||
                    `${capitalize(copy[COPY_KEYS.BUTTONS.CREATE])} ${copy[COPY_KEYS.COMMON.A]} ${copy[
                      COPY_KEYS.HEADINGS.CUSTOMER
                    ].toLowerCase()} ${copy[COPY_KEYS.HEADINGS.SITE].toLowerCase()}`}
                </Checkbox.Label>
              </Checkbox.Root>
            </Field>

            <Collapsible.Root open={createSite} width="100%">
              <Collapsible.Content width="100%">
                <VStack gap={4} width="100%">
                  <Field
                    label={`${copy[COPY_KEYS.HEADINGS.SITE]} ${copy[COPY_KEYS.LABELS.NAME]}`}
                    required={createSite}
                  >
                    <Input
                      value={siteName}
                      onChange={(e) => setSiteName(e.target.value)}
                      placeholder={composePlaceholder(
                        copy,
                        COPY_KEYS.FORMS.PLACEHOLDER_ENTER_NAME,
                        COPY_KEYS.HEADINGS.SITE
                      )}
                    />
                  </Field>
                </VStack>
              </Collapsible.Content>
            </Collapsible.Root>
          </>
        )}
      </VStack>
    </Form>
  )
}

export default CustomerCreationForm
